// frontend/src/components/layout/Sidebar.jsx
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isOpen, isMobile, onToggle, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, hasAnyPermission } = useAuth();

  // Цэсүүд ба шаардлагатай эрхүүд
  const allMenuItems = [
    {
      title: 'Dashboard',
      path: '/admin/dashboard',
      icon: '📊',
      permission: 'system.dashboard.view',
    },
    {
      title: 'Компаниуд',
      path: '/admin/companies',
      icon: '🏢',
      permission: 'system.companies.view',
    },
    {
      title: 'Төлбөр тооцоо',
      path: '/admin/settlements',
      icon: '💰',
      permission: 'system.settlements.view',
    },
    {
      title: 'Тайлан',
      path: '/admin/reports',
      icon: '📈',
      permission: 'system.reports.view',
    },
    {
      title: 'Хэрэглэгч',
      path: '/admin/users',
      icon: '👥',
      permission: 'system.users.view',
    },
    {
      title: 'Тохиргоо',
      path: '/admin/settings',
      icon: '⚙️',
      permission: 'system.settings.view',
    },
  ];

  // Эрхтэй цэсүүдийг шүүх
  const menuItems = (allMenuItems || []).filter(item => {
    // Super admin бол бүгдийг харуулна
    if (user?.is_super_admin) return true;
    // Эрх шалгах - hasPermission undefined байвал false буцаана
    try {
      return hasPermission ? hasPermission(item.permission) : false;
    } catch (e) {
      console.error('Permission check error:', e);
      return false;
    }
  });

  const handleLogout = () => {
    if (confirm('Гарахдаа итгэлтэй байна уу?')) {
      logout();
      navigate('/login');
    }
  };

  const handleLinkClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white shadow-lg fixed top-0 left-0 h-full z-50
          transition-transform duration-300 ease-in-out
          ${isMobile 
            ? (isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64') 
            : (isOpen ? 'w-64' : 'w-20')
          }
          ${!isMobile ? 'relative' : ''}
        `}
      >
        {/* Close button for mobile */}
        {isMobile && isOpen && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition z-50"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          {(isOpen || isMobile) ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                🚀 <span className="bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">Pingo</span>
              </h1>
              <p className="text-sm text-gray-500 mt-1">Системийн админ</p>
            </>
          ) : (
            <div className="text-center">
              <span className="text-3xl">🚀</span>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="mt-6 flex-1 overflow-y-auto px-3">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleLinkClick}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
                location.pathname === item.path || 
                (item.path === '/admin/settlements' && location.pathname.startsWith('/admin/settlements')) ||
                (item.path === '/admin/companies' && location.pathname.startsWith('/admin/companies'))
                  ? 'bg-primary-50 text-primary-600 font-semibold' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title={!isOpen && !isMobile ? item.title : ''}
            >
              <span className="text-xl flex-shrink-0">{item.icon}</span>
              {(isOpen || isMobile) && <span className="font-medium">{item.title}</span>}
            </Link>
          ))}
        </nav>

        {/* Toggle Button - Hide on mobile */}
        {!isMobile && (
          <div className="px-4 py-4">
            <button
              onClick={onToggle}
              className="w-full p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition text-gray-700 flex items-center justify-center gap-2"
              title={isOpen ? 'Хураах' : 'Дэлгэх'}
            >
              <span>{isOpen ? '⬅️' : '➡️'}</span>
              {isOpen && <span className="text-sm font-medium">Хураах</span>}
            </button>
          </div>
        )}

        {/* User Info */}
        <div className="border-t border-gray-200 bg-white p-4">
          {(isOpen || isMobile) ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  {user?.full_name?.charAt(0) || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.is_super_admin ? 'Super Admin' : user?.role || 'Системийн админ'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition text-sm font-medium shadow-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Гарах
              </button>
            </>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full p-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition flex items-center justify-center"
              title="Гарах"
            >
              🚪
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;